<script lang="ts">
  /**
   * Мульти-селект по п'яти UTM-вимірах — спільний для фільтра оплат і фільтра
   * контактів. `scope` перемикає джерело значень: 'payments' читає мітки самих
   * оплат, 'contacts' — first-touch контактів.
   *
   * Секції згорнуті за замовчуванням і вантажать значення ліниво: на відкритті
   * модалки інакше йшло б п'ять запитів, з яких чотири зазвичай не потрібні.
   * utm_content / utm_term часто містять ad_id (сотні значень), тому список
   * обрізаний до топ-N, а решта дістається пошуком.
   */
  import { api } from '../lib/api';
  import { UTM_DIMENSIONS, UTM_DIMENSION_LABEL, type UtmDimension, type UtmSelection } from '../lib/types';

  let {
    scope = 'payments',
    selected = $bindable(),
  }: {
    scope?: 'payments' | 'contacts';
    selected: UtmSelection;
  } = $props();

  /** Сентинел «без мітки» — дзеркалить FILTER_NONE в API. */
  const NONE = '__none__';
  const LIMIT = 50;

  type Values = { items: { value: string; count: number }[]; hasNone: boolean };

  let open = $state<Record<string, boolean>>({});
  let values = $state<Record<string, Values | null>>({});
  let search = $state<Record<string, string>>({});
  let loading = $state<Record<string, boolean>>({});

  const timers: Record<string, ReturnType<typeof setTimeout> | undefined> = {};

  async function load(dim: UtmDimension): Promise<void> {
    loading[dim] = true;
    try {
      const r = await api.adminFilterValues({
        field: dim,
        scope,
        q: search[dim] || undefined,
        limit: LIMIT,
      });
      values[dim] = r;
    } catch {
      values[dim] = { items: [], hasNone: false };
    } finally {
      loading[dim] = false;
    }
  }

  function toggleSection(dim: UtmDimension): void {
    open[dim] = !open[dim];
    if (open[dim] && values[dim] === undefined) void load(dim);
  }

  function onSearch(dim: UtmDimension, v: string): void {
    search[dim] = v;
    clearTimeout(timers[dim]);
    timers[dim] = setTimeout(() => void load(dim), 300);
  }

  function toggleValue(dim: UtmDimension, value: string): void {
    const cur = selected[dim] ?? [];
    const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
    // Порожній масив прибираємо, щоб `selected` не накопичував порожні ключі —
    // від цього залежить підрахунок активних фільтрів на сторінках.
    selected = next.length ? { ...selected, [dim]: next } : omit(selected, dim);
  }

  function omit(s: UtmSelection, dim: UtmDimension): UtmSelection {
    const out = { ...s };
    delete out[dim];
    return out;
  }

  function isOn(dim: UtmDimension, value: string): boolean {
    return (selected[dim] ?? []).includes(value);
  }

  // Секція з уже обраними значеннями відкривається сама — інакше після
  // повернення у фільтр не видно, що саме там вибрано. `autoOpened` — звичайний
  // Set, а не $state: інакше ефект перевідкривав би секцію щоразу, коли її
  // згортають вручну, і закрити її з активним вибором стало б неможливо.
  const autoOpened = new Set<string>();
  $effect(() => {
    for (const dim of UTM_DIMENSIONS) {
      if ((selected[dim] ?? []).length && !autoOpened.has(dim)) {
        autoOpened.add(dim);
        open[dim] = true;
        if (values[dim] === undefined) void load(dim);
      }
    }
  });
</script>

{#each UTM_DIMENSIONS as dim (dim)}
  {@const count = (selected[dim] ?? []).length}
  <button type="button" class="utm-head" onclick={() => toggleSection(dim)}>
    <span class="utm-label">{UTM_DIMENSION_LABEL[dim]}</span>
    {#if count > 0}<span class="utm-badge">{count}</span>{/if}
    <span class="utm-chevron" class:open={open[dim]}>›</span>
  </button>

  {#if open[dim]}
    <input
      class="utm-search"
      type="text"
      placeholder="Пошук значення…"
      value={search[dim] ?? ''}
      oninput={(e) => onSearch(dim, (e.target as HTMLInputElement).value)}
    />

    {#if values[dim] == null}
      <div class="utm-placeholder">Завантажуємо…</div>
    {:else if values[dim].items.length === 0 && !values[dim].hasNone}
      <div class="utm-placeholder">
        {search[dim] ? 'Нічого не знайдено' : 'Ще немає значень'}
      </div>
    {:else}
      <!-- Під час повторного пошуку список лишається на місці й лише блідне —
           так не смикається висота модалки на кожне натискання клавіші. -->
      <div class="utm-pills" class:searching={loading[dim]}>
        {#each values[dim].items as v (v.value)}
          <button
            type="button"
            class="utm-pill"
            class:active={isOn(dim, v.value)}
            onclick={() => toggleValue(dim, v.value)}
          >{v.value} <span class="utm-pill-count">{v.count}</span></button>
        {/each}
        {#if values[dim].hasNone}
          <button
            type="button"
            class="utm-pill"
            class:active={isOn(dim, NONE)}
            onclick={() => toggleValue(dim, NONE)}
          >Без мітки</button>
        {/if}
      </div>
      {#if values[dim].items.length >= LIMIT}
        <div class="utm-hint">Показано топ-{LIMIT} значень — решту шукайте пошуком.</div>
      {/if}
    {/if}
  {/if}
{/each}

<style>
  .utm-head {
    display: flex; align-items: center; gap: 8px; width: 100%;
    background: none; border: none; padding: 10px 0; cursor: pointer;
    font-family: inherit; color: var(--text-muted);
    border-top: 0.5px solid var(--border);
  }
  .utm-label {
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px;
  }
  .utm-badge {
    background: var(--pu); color: var(--text-light); font-size: 10px; font-weight: 700;
    min-width: 16px; height: 16px; border-radius: 999px;
    display: inline-flex; align-items: center; justify-content: center; padding: 0 5px;
  }
  .utm-chevron {
    margin-left: auto; font-size: 16px; line-height: 1;
    transition: transform 0.15s var(--ease);
  }
  .utm-chevron.open { transform: rotate(90deg); }
  .utm-search {
    width: 100%; box-sizing: border-box; margin-bottom: 8px;
    border: 1px solid var(--border2); border-radius: 8px; padding: 8px 10px;
    font-size: 12px; color: var(--text); background: var(--bg);
    font-family: inherit; outline: none;
  }
  .utm-search:focus { border-color: var(--outline); }
  .utm-pills {
    display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 4px;
    transition: opacity 0.15s var(--ease);
  }
  .utm-pills.searching { opacity: 0.5; }
  .utm-pill {
    border: 1px solid var(--border2); background: var(--bg); color: var(--text);
    padding: 7px 12px; border-radius: 999px; font-size: 12px; font-weight: 500;
    cursor: pointer; font-family: inherit; max-width: 100%;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    transition: background 0.15s var(--ease), color 0.15s var(--ease), border-color 0.15s var(--ease);
  }
  .utm-pill.active { background: var(--pu); color: var(--text-light); border-color: var(--pu); }
  .utm-pill-count { opacity: 0.6; font-size: 11px; }
  .utm-placeholder {
    padding: 12px; text-align: center; color: var(--text-muted); font-size: 12px;
    border: 1px dashed var(--border2); border-radius: 10px; background: var(--bg2);
    margin-bottom: 4px;
  }
  .utm-hint { font-size: 11px; color: var(--text-muted); margin: 2px 0 6px; }
</style>
